import React, { useState, useContext, useEffect, useRef } from "react";
import { hasRole } from "../utils/guestUser";
import ReactApexChart from "react-apexcharts";
import { AuthContext } from "../context/AuthContext";
import { Select, MenuItem } from "@mui/material";
import { UserContext } from "../context/UserContext";
import ToastComponent from "./ToastComponent";
import { useNavigate } from "react-router-dom";
import {
  clientApiUrl,
  taskApiUrl,
  notificationApiUrl,
  userApiUrl,
} from "../utils/GetUrl";
import "../Styles/graph.css";

function GraphPage() {
  const navigate = useNavigate();
  const { token, logout, userData } = useContext(AuthContext);
  const { teamsData, usersList } = useContext(UserContext);

  const [monthlyValues, setMonthlyValues] = useState(new Array(12).fill(0));
  const [controlData, setControlData] = useState(new Array(12).fill(0));

  const [selectedTeam, setSelectedTeam] = useState(
    (hasRole(userData?.[0]?.DR_Name, "Admin") || hasRole(userData?.[0]?.DR_Name, "Super Admin") || hasRole(userData?.[0]?.DR_Name, "Managing Director"))
      ? ""
      : teamsData[0]?.filter((t) => t.id == userData?.[0]?.STM_Team)?.[0]?.name ?? ""
  );

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedId, setselectedId] = useState(
    (hasRole(userData?.[0]?.DR_Name, "Admin") || hasRole(userData?.[0]?.DR_Name, "Super Admin") || hasRole(userData?.[0]?.DR_Name, "Managing Director")) ? "" : userData?.[0]?.STM_Team
  );

  const [filteredEmployeeList, setFilteredEmployeeList] = useState("");

  const toastRef = useRef();
  const isFetchingRef = useRef(false); // ✅ ADD

  useEffect(() => {
    const filteredEmployees = usersList?.filter(
      (user) => user?.STM_Team == selectedId
    );

    setFilteredEmployeeList(
      filteredEmployees?.map((item) => `'${item["EM_ID"]}'`).join(",") || ""
    );
  }, [selectedId, usersList]);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Decs",
  ];

  const years = Array.from({ length: 10 }, (_, index) => 2020 + index);
  const currentYear = new Date().getFullYear();

  if (!years.includes(currentYear)) {
    years.push(currentYear);
    years.sort((a, b) => a - b);
  }

  useEffect(() => {
    setSelectedYear(currentYear);
  }, [currentYear]);

  const handleChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleTeamChange = (e) => {
    setSelectedTeam(e.target.value);
    const selectedTeam = teamsData[0]?.find(
      (team) => team.name === e.target.value
    );
    setselectedId(selectedTeam?.id || "");
  };

  const fetchForecast = async () => {
    if (!selectedYear) return;

    try {
      const response = await fetch(
        `${userApiUrl}/GetLatestBudget?Year=${selectedYear}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        toastRef.current.showToast(
          "Unauthorized access. Please login again.",
          "error"
        );
        await logout();
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch budget history data");
      }

      const data = await response.json();
      processForecastData(data);
    } catch (error) {
      console.error("Error fetching budget history data:", error);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, [selectedYear, selectedTeam]); // ❌ token REMOVED

  const processForecastData = (data) => {
    const filteredData = selectedTeam
      ? data.filter((item) => item.Team === selectedTeam)
      : data;

    const newMonthlyValues = months.map((month) =>
      filteredData.reduce((sum, item) => sum + (item[month] || 0), 0)
    );

    setMonthlyValues(newMonthlyValues);
  };

  const fetchControlData = async () => {
    if (!selectedYear) return;
    try {
      const url = filteredEmployeeList
        ? `${taskApiUrl}/GetCapacityUtilisation?CreatedYear=${selectedYear}&AssginedToEmids=${filteredEmployeeList}`
        : `${taskApiUrl}/GetCapacityUtilisation?CreatedYear=${selectedYear}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      processControlData(data);
    } catch (error) {
      console.error("Error fetching capacity utilisation data:", error);
    }
  };

  const processControlData = (data) => {
    const currentMonth = new Date().getMonth();
    const newControlData = new Array(12).fill(0);

    data.forEach((item) => {
      const monthIndex = parseInt(item.CreatedMonth, 10) - 1;

      if (monthIndex >= 0 && monthIndex <= currentMonth) {
        // ✅ SUM all TotalTime for the same month
        newControlData[monthIndex] += Number(item.TotalTime) || 0;
      }
    });

    // ✅ Divide each month's total by 60
    const finalControlData = newControlData.map((value) =>
      Number((value / 60).toFixed(2))
    );

    setControlData(finalControlData);
  };

  useEffect(() => {
    if (selectedYear && !isFetchingRef.current) {
      isFetchingRef.current = true;
      fetchControlData();
    }
  }, [selectedYear, selectedTeam, filteredEmployeeList]);

  useEffect(() => {
    isFetchingRef.current = false;
  }, [selectedYear, selectedTeam]);

  const seriesData = [
    { name: "Budget", data: monthlyValues },
    { name: "Actual", data: controlData },
  ];

  const optionsData = {
    chart: { type: "bar", stacked: false },
    colors: ["#50145a", "#B19CD9"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    },
    yaxis: {
      labels: { formatter: (value) => Math.floor(value) },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "40%" },
    },
    legend: { position: "top" },
  };

  return (
    <div className="App-graph-1-dash">
      <div className="graph1-container">
        <div className="graph-1-heading">MONTHLY FORECAST</div>

        <div className="graph1-drop">
          <div className="graph1-drop2">
            <Select value={selectedYear} onChange={handleChange} fullWidth>
              <MenuItem value="" disabled>
                Select Year
              </MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>

            {(hasRole(userData?.[0]?.DR_Name, "Admin") || hasRole(userData?.[0]?.DR_Name, "Super Admin") || hasRole(userData?.[0]?.DR_Name, "Managing Director") || !hasRole(userData?.[0]?.DR_Name, "Consultant")) && (
              <Select
                fullWidth
                value={selectedTeam}
                onChange={handleTeamChange}
              >
                <MenuItem value="">All Teams</MenuItem>
                {teamsData[0]
                  ?.filter((team) => team?.status && team?.type === "T")
                  ?.map((data) => (
                    <MenuItem key={data.id} value={data.name}>
                      {data.name}
                    </MenuItem>
                  ))}
              </Select>
            )}
          </div>
        </div>
      </div>

      <div className="graph-2-subhead">12 months forecast</div>

      <ReactApexChart
        series={seriesData}
        options={optionsData}
        type="bar"
        height={350}
      />
    </div>
  );
}

export default GraphPage;
